import { Group } from "common/associations/association-group.service";
import { Association } from "common/associations/association-list.component";
import { AssociationStore } from "common/associations/association-store.interface";
import { AssocSearchResult } from "common/associations/search-query.logic";
import { ProcessElementsResource } from "core/stages-client";
import { ViewService } from "core/view.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AssociationService } from "process/associations/association.service";
import { ProcessComplianceAdapter } from "process/compliance/associations/process-compliance-association-store.logic";

type TargetElement = stages.compliance.TargetElement;

export class ComplianceProcessAssociationStore implements AssociationStore<ProcessComplianceAdapter, TargetElement> {
	constructor(
		protected viewService: ViewService,
		protected associationService: AssociationService,
		readonly processElementsResource: ProcessElementsResource,
	) {}

	getAddMenuEntryNameKey(_group: Group<TargetElement>): string {
		return "undefined";
	}

	getAddMenuEntryNameKeyParam(_group: Group<TargetElement>): string | undefined {
		return undefined;
	}

	getAddMenuEntryNameKeyParamValue(group: Group<TargetElement>): string | undefined {
		return group.name;
	}

	getInputClasses(
		typeIdent: string | undefined,
		isDependentElement: boolean,
		subtypeIdent: string | null,
		// eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents -- TODO: Remove this comment and fix warnings!
		additionalClasses?: string | undefined,
	): string[] {
		if (typeIdent) {
			// Icon for search results dropdown items
			return this.viewService.getIconClasses(
				{
					ident: typeIdent,
					subtypeIdent: subtypeIdent,
				},
				false,
				isDependentElement,
				additionalClasses,
			);
		}
		// Icon for search input text box
		return ["ico", "ico-compliance"];
	}

	getIconClasses(
		targetElement: TargetElement,
		isDependentElement: boolean,
		// eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents -- TODO: Remove this comment and fix warnings!
		additionalClasses?: string | undefined,
	): string[] {
		return this.viewService.getIconClasses(
			targetElement.type,
			targetElement.index,
			isDependentElement,
			additionalClasses,
		);
	}

	getLabelClasses(
		_sourceElement: ProcessComplianceAdapter,
		targetElement?: TargetElement,
		// eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents -- TODO: Remove this comment and fix warnings!
		_additionalClasses?: string | undefined,
	): string[] {
		return targetElement?.tailored ? ["tailored"] : [];
	}

	// eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents -- TODO: Remove this comment and fix warnings!
	getQuickAssignLabelClasses(searchResult: AssocSearchResult, _additionalClasses?: string | undefined): string[] {
		return searchResult.tailored ? ["tailored"] : [];
	}

	getActions(_association: Association<TargetElement>): StringToBoolean {
		return {};
	}

	getTargetElementActions(_targetElement: TargetElement): StringToBoolean {
		return {};
	}

	getSourceElement(
		_type: string,
		_identity: string,
		_workspaceId: string,
		_pv: string,
	): Observable<ProcessComplianceAdapter> {
		return this.viewService.awaitSelfElementObservable().pipe(map((self) => new ProcessComplianceAdapter(self)));
	}

	getAdditionalSourceElements(_sourceElement: ProcessComplianceAdapter): ProcessComplianceAdapter[] {
		return [];
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async getParentForNewTargetElement(
		_sourceElement: ProcessComplianceAdapter,
		_targetType: string,
	): Promise<TargetElement | null> {
		throw new Error("Method not implemented.");
	}

	asTypedId(sourceElement: ProcessComplianceAdapter): stages.core.TypedId {
		return {
			typeIdent: sourceElement.type.ident,
			id: sourceElement.id,
		};
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async createAssociationToNewTargetElement(
		_sourceElement: stages.core.TypedId,
		_newElementParent: TargetElement,
		_targetType: string,
		_targetSubtype: string | null,
		_name: string,
		_associationType: string,
		_sourceRole: string | undefined,
		_workspaceId: string,
		_pv: string,
	): Promise<Association<TargetElement>> {
		throw new Error("Method not implemented.");
	}

	createElementAndChangeAssociationSource(
		_sourceElementParent: ProcessComplianceAdapter,
		_sourceType: string,
		_sourceSubtype: string,
		_name: string,
		_workspaceId: string,
		_pv: string,
		_dependent: boolean,
		_associationId: string,
		_targetElementType: string,
		_targetElementId: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	changeAssociationSourceOrTarget(
		_sourceElement: ProcessComplianceAdapter,
		_associationId: string,
		_sourceElementType: string,
		_sourceElementId: string,
		_targetElementType: string,
		_targetElementId: string,
		_workspaceId: string,
		_pv: string,
		_associationPath: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	createElementAndChangeAssociationTarget(
		_newElementParent: TargetElement,
		_targetType: string,
		_targetSubtype: string,
		_name: string,
		_workspaceId: string,
		_pv: string,
		_dependent: boolean,
		_associationId: string | undefined,
		_associationType: string,
		_sourceRole: string | undefined,
		_sourceElementType: string,
		_sourceElementId: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async createAssociation(
		_sourceElement: stages.core.TypedId,
		_targetElementId: string,
		_targetElementType: string,
		_associationType: string,
		_sourceRole: string | undefined,
		_currentWorkspaceId: string,
		_pv: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async createCommentOnlyAssociation(
		_sourceElement: stages.core.TypedId,
		_targetType: string,
		_targetSubtype: string | null,
		_associationType: string,
		_sourceRole: string | undefined,
		_workspaceId: string,
		_pv: string,
		_comment: string,
	): Promise<Association<TargetElement>> {
		throw new Error("Method not implemented.");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async deleteAssociations(
		_associationsToDelete: Association<TargetElement>[],
		_workspaceId: string,
		_pv: string,
	): Promise<Association<TargetElement>[]> {
		throw new Error("Method not implemented.");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async updateComment(
		_associationId: string,
		_comment: string,
		_currentWorkspaceId: string,
		_pv: string,
	): Promise<void> {
		throw new Error("Method not implemented.");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async searchProcessElements(
		_searchTerm: string,
		_targetElementType: string,
		_targetElementSubtypes: string[],
		_targetElementsToIgnore: TargetElement[],
		_currentWorkspaceId: string,
		_searchWorkspaceId: string,
		_pv: string,
	): Promise<AssocSearchResult[]> {
		throw new Error("Method not implemented.");
	}

	searchDependentProcessElements(
		_searchTerm: string,
		_targetElementType: string,
		_containerElementId: string,
		_targetElementsToIgnore: TargetElement[],
		_currentWorkspaceId: string,
		_pv: string,
	): Promise<AssocSearchResult[]> {
		throw new Error("Method not implemented.");
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async applyAssociationModifications(
		_sourceElement: ProcessComplianceAdapter,
		_groupPath: string,
		_added: stages.core.TypedId[],
		_removed: stages.core.TypedId[],
		_modifiedCommentJson: stages.core.model.TypedIdAssociationDetails[],
		_processModelWorkspaceId: string,
		_pv: string,
		_referenceModelWorkspaceId: string,
	): Promise<Association<TargetElement>[]> {
		throw new Error("Method not implemented.");
	}

	getLink(association: Association<TargetElement>, pv: string): unknown[] {
		if (association.targetElement) {
			return [
				"/",
				"workspace",
				association.targetElement.workspaceId,
				pv,
				"process",
				association.targetElement.type.ident,
				association.targetElement.identity,
			];
		}
		return [];
	}

	isSelectableInBrowser(
		_existingAssociation: Association<TargetElement> | null,
		_sourceElement: ProcessComplianceAdapter,
		_targetElement: TargetElement,
		_targetSubtypes: string[] | undefined,
	): boolean {
		return false;
	}

	reset(): void {
		throw new Error("not implemented");
	}

	getDetailLink(
		sourceElement: ProcessComplianceAdapter,
		association: Association<TargetElement | null>,
		path: string,
		_groupBy?: string,
		_mode?: string,
	): unknown[] {
		if (association.isCommentOnly) {
			return [
				"process",
				"compliancecommentcombined",
				sourceElement.type.ident,
				sourceElement.identity,
				"detailView",
				association.id,
				path,
				{ targetElementLabel: association.comment },
			];
		}
		return [
			"process",
			"compliancecombined",
			sourceElement.type.ident,
			sourceElement.identity,
			"detailView",
			association.targetElement!.identity,
			association.id,
			path,
			{ targetElementLabel: association.targetElement!.label },
		];
	}

	async getAssociationGroupTranslateable(_path: string, _groupBy: string | null): Promise<string> {
		return Promise.resolve("compliance.references");
	}

	async getCombinedAssociations(
		targetElementIdentities: string[],
		currentWorkspaceId: string,
		sourceElementType: string,
		sourceElementIdentity: string,
		path: string,
		page: number,
		pageSize: number,
		pv: string,
	): Promise<SecuredPagedResult<stages.common.Association<stages.process.RemoteTargetElement>>> {
		return this.associationService.getCombinedAssociations(
			targetElementIdentities,
			currentWorkspaceId,
			sourceElementType,
			sourceElementIdentity,
			path,
			page,
			pageSize,
			pv,
		);
	}

	async getTree(browseWorkspaceId: string, elementTypes: string[]): Promise<stages.process.TreeElement[]> {
		return this.processElementsResource.getTree(browseWorkspaceId, elementTypes[0], { elementTypes: elementTypes });
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
	async rearrangeAssociations(
		_currentWorkspaceId: string,
		_sortedAssociationIds: string[],
		_sortStrategy: stages.core.sort.SortStrategy,
		_type: string,
		_identity: string,
		_path: string,
		_groupBy: string,
		_pv: string,
	): Promise<Record<string, never>> {
		throw new Error("Method not implemented.");
	}

	getGroupIdentifier(groupPath: string, _groupBy?: string): string {
		return groupPath;
	}
}
